import {FC, useCallback, useContext} from "react";
import {makeStyles} from "tss-react/mui";
import {Box, Typography} from "@mui/material";
import {AdminHeader} from "../../../AdminHeader";
import {useNavigate} from "react-router-dom";
import {useApi} from "@buildwitt/shapeshift-identity";
import {IAdminUsersCoursesResponseEntry, IEntity, ILocalizationEntity} from "@buildwitt/shapeshift-types";
import {AdminTable, AdminTableProps} from "@/components/common/table";
import {ConfirmDeleteEntityDialog} from "@buildwitt/shapeshift-common/v2/admin";
import {GRID_CHECKBOX_SELECTION_COL_DEF} from "@mui/x-data-grid-pro";
import {PageBaseContext} from "@/contexts/pageBaseContext";
import {CourseBlacklistToggle} from "./CourseBlacklistToggle";
import useDevCycleSdk from "@/hooks/useDevCycleSdk";
import { IBaseSearchDTO } from "@/components/common/table/AdminTable";

const useStyles = makeStyles()((_theme) => ({
	adminCoursesList: {
		"& th": {
			fontSize: "11px",
			fontWeight: 700,
			textTransform: "uppercase"
		}
	}
}));



export const AdminCoursesList: FC = () => {
	const {classes} = useStyles();
	const navigate = useNavigate();
	const [locales] = useApi<ILocalizationEntity[]>("/api/settings/i18n");
	const [{ownedCohort}] = useContext(PageBaseContext)
	const {hideCreationButtons, contentCreationUpdates} = useDevCycleSdk();

	const headCells = (locales?: ILocalizationEntity[], ownedCohortTitle?: string, cohortId?: number): AdminTableProps<"course", IAdminUsersCoursesResponseEntry>["columns"] => ([
		{
			...GRID_CHECKBOX_SELECTION_COL_DEF,
			type: "string",
			hideable: false
		},
		{
			field: "title",
			type: "string",
			flex: 1,
			headerName: "Course",
			minWidth: 240
		},
		{
			field: "moduleCount",
			type: "string",
			flex: 1,
			headerName: "Lessons",
			maxWidth: 110
		},
		{
			field: "cohortMembersEnrollmentCount",
			type: "number",
			sortable: false,
			align: "left",
			filterable: false,
			flex: 1,
			headerName: "Internal Enrollments"
		},
		{
			field: "completionCount",
			type: "string",
			filterable: false,
			flex: 1,
			headerName: "Course Completions"
		},
		{
			field: "contentType",
			type: "string",
			flex: 1,
			headerName: "Content Type"
		},
		{
			field: "updatedAt",
			type: "date",
			flex: 1,
			headerName: "Last Modified",
			valueGetter: (e) => e.value ? new Date(e.value).toLocaleDateString() : ""
		},
		{
			field: "publishedAt",
			type: "date",
			flex: 1,
			headerName: "Published",
			valueGetter: (e) => e.value ? new Date(e.value).toLocaleDateString() : ""
		},
		{
			field: "authorCohort",
			type: "string",
			flex: 1,
			filterable: false,
			headerName: "Shared By",
			valueGetter: ({value}: {value: IAdminUsersCoursesResponseEntry["authorCohort"]}) => (value?.title ?? "") === ownedCohortTitle ? "" : value?.title
		},
		{
			field: "isBlacklisted",
			type: "boolean",
			flex: 1,
			sortable: false,
			headerName: "Hide From Team",
			renderCell: e => <CourseBlacklistToggle courseId={e.id as number} initialState={e.value} />
		}
	]);

	const cohort = typeof ownedCohort === "object" ? ownedCohort : undefined;

	const onCreateClick = useCallback(() => {
		if(contentCreationUpdates) {
			navigate("create-new");
		} else {
			navigate("create");
		}
	}, [contentCreationUpdates, navigate]);

	return (
		<Box className={classes.adminCoursesList}>
			<AdminHeader
				title="Courses"
				subtitle="Manage courses, enroll them into courses and check their capacity"
				actionButtons={hideCreationButtons ? undefined : [{label: "Create New", onClick: onCreateClick}]}
			/>
			<AdminTable<"course", IAdminUsersCoursesResponseEntry, IBaseSearchDTO<IEntity> & {extendedAccess?: boolean}>
				url="/api/strapi-plugin-shapeshift-lms/cohort/course"
				columns={headCells(locales.data, cohort?.title, cohort?.id)}
				searchDTOverride={(() => ({extendedAccess: true}))}
				onEditAction={e => navigate(e.id.toString())}
				enableDelete={e => !!(e.authorCohort && ownedCohort && e.authorCohort.id === ownedCohort.id)}
				type="course"
				filterSettingsPersistenceStorageKey="courseFilterSettings"
				componentOverrides={{
					deleteDialog: deleteProps => <ConfirmDeleteEntityDialog
						{...deleteProps}
						componentOverrides={{
							content: (deleteProps?.entity?.enrollmentCount && deleteProps.entity?.enrollmentCount >= 1) ? <Typography variant="body1">You will be making an assigned course unavailable.</Typography> : undefined
						}}
					/>
				}}
				initialState={{
					columns: {
						columnVisibilityModel: {
							tags: false,
							updatedAt: false
						}}}}
			/>
		</Box>
	);
}
